import Card from "components/card";
import { FactoryFormScheme } from "./scheme";
import { FormProvider, useForm } from "react-hook-form";
import InputField from "components/fields/InputField";
import { Button } from "components/Button";
import { useFactoryContract } from "hooks/useFactoryContract";
import { useSolanaFactoryContract } from "hooks/useSolanaFactoryContract";
import { isAddress, parseUnits } from "ethers";
import { useAccount } from "wagmi";
import { usePoolProvider } from "context";
import { useCallback, useEffect } from "react";
import { useNetworksStore } from "store/networksStore";
import { Loader } from "components/Loader";
import { shortenAddress } from "utils/shortenAddress";
import { polygonId } from "../../../constants";
import { Fee } from "../../../types/fee";
import { FeeComponent } from "./components/FeeComponent";
import { isSolanaNetwork } from "types/networks";
import { useSolanaWallet } from "hooks/useSolanaWallet";
import { PublicKey } from "@solana/web3.js";

const isValidSolanaAddress = (address: string): boolean => {
  try {
    new PublicKey(address);
    return true;
  } catch {
    return false;
  }
};

export const FactorySettings = () => {
  const { address: evmAccount } = useAccount();
  const { address: solanaAccount } = useSolanaWallet();
  const { jsonRpcProvider } = usePoolProvider();
  const { currentNetwork } = useNetworksStore();
  const isCurrentSolanaNetwork = isSolanaNetwork(currentNetwork);
  const account = isCurrentSolanaNetwork ? solanaAccount : evmAccount;

  const currencyDecimals = currentNetwork?.depositTokens?.[0]?.decimals ?? 18;

  const defaultFee = {
    from: "",
    percentage: "",
    fixedFee: "",
  };

  const evmFactoryContract = useFactoryContract(jsonRpcProvider);
  const solanaFactoryContract = useSolanaFactoryContract();
  const selectedFactoryContract = isCurrentSolanaNetwork
    ? solanaFactoryContract
    : evmFactoryContract;

  const {
    getFactoryState,
    poolCreationFeeValue,
    factoryOwner,
    setPoolCreationFee,
    isLoadingState,
    setFeesInfo,
  } = selectedFactoryContract;

  const { xbrFeeValue, setXBRDiscountNumerator, sweepToken } =
    evmFactoryContract;
  const {
    treasuryAddress: solanaTreasuryAddress,
    jupiterProgramAddress: solanaJupiterProgramAddress,
    swapExecutorAddress: solanaSwapExecutorAddress,
    operationCount: solanaOperationCount,
    setTreasury,
    setJupiterProgram,
    setSwapExecutor,
    migrateFactory,
  } = solanaFactoryContract;

  const form = useForm<FactoryFormScheme>({
    values: {
      creationFee: "",
      stakingFeeFlex: Array.from({ length: 5 }, () => ({ ...defaultFee })),
      unstakingFeeFlex: Array.from({ length: 5 }, () => ({ ...defaultFee })),
      xbrDiscount: "",
      treasuryAddress: "",
      jupiterProgramAddress: "",
      swapExecutorAddress: "",
      migrationOperationCount: "",
      migrationSwapExecutorAddress: "",
      sweepMintAddress: "",
      sweepAmount: "",
      address: "",
      submiting: false,
    },
  });

  const xbrDiscount = form.watch("xbrDiscount");
  const creationFee = form.watch("creationFee");
  const treasuryAddress = form.watch("treasuryAddress");
  const jupiterProgramAddress = form.watch("jupiterProgramAddress");
  const swapExecutorAddress = form.watch("swapExecutorAddress");
  const migrationOperationCount = form.watch("migrationOperationCount");
  const migrationSwapExecutorAddress = form.watch("migrationSwapExecutorAddress");
  const sweepMintAddress = form.watch("sweepMintAddress");
  const sweepAmount = form.watch("sweepAmount");
  const address = form.watch("address");
  const submiting = form.watch("submiting");
  const stakingFeeFlex = form.watch("stakingFeeFlex");
  const unstakingFeeFlex = form.watch("unstakingFeeFlex");

  const getFactorySettings = useCallback(async () => {
    try {
      const state = await getFactoryState();
      if (!Array.isArray(state)) {
        return;
      }

      const [stakingFeeFlex, unstakingFeeFlex] = state;

      if (stakingFeeFlex?.length) {
        form.setValue("stakingFeeFlex", stakingFeeFlex);
      }

      if (unstakingFeeFlex?.length) {
        form.setValue("unstakingFeeFlex", unstakingFeeFlex);
      }
    } catch (error) {
      console.error("Error fetching factory settings:", error);
    }
  }, [form, getFactoryState]);

  useEffect(() => {
    getFactorySettings();
  }, [
    jsonRpcProvider,
    currentNetwork?.id,
    isCurrentSolanaNetwork,
    getFactorySettings,
  ]);

  const handleSetXBRDiscounteNumerator = async (xbrDiscount: string) => {
    if (
      !xbrDiscount ||
      !parseFloat(xbrDiscount) ||
      parseFloat(xbrDiscount) > 100
    )
      return;

    try {
      form.setValue("submiting", true);
      await setXBRDiscountNumerator(parseFloat(xbrDiscount) * 100);

      form.setValue("xbrDiscount", "");

      form.setValue("submiting", false);
      await getFactorySettings();
    } catch (error) {
      console.error(error);
      form.setValue("submiting", false);
    }
  };

  const handleSetPoolCreationFee = async (creationFee: string) => {
    if (!creationFee || !parseFloat(creationFee)) return;

    try {
      form.setValue("submiting", true);
      await setPoolCreationFee(parseUnits(creationFee, currencyDecimals));
      await getFactorySettings();

      form.setValue("submiting", false);
    } catch (error) {
      console.error(error);
      form.setValue("submiting", false);
    }
  };

  const handleSweepToken = async (address: string) => {
    if (!isAddress(address)) return;

    try {
      form.setValue("submiting", true);
      await sweepToken(address);
      await getFactorySettings();

      form.setValue("address", "");

      form.setValue("submiting", false);
    } catch (error) {
      console.error(error);
      form.setValue("submiting", false);
    }
  };

  const handleSetTreasury = async (nextTreasuryAddress: string) => {
    if (!isValidSolanaAddress(nextTreasuryAddress)) return;

    try {
      form.setValue("submiting", true);
      await setTreasury(nextTreasuryAddress);
      await getFactorySettings();
      form.setValue("treasuryAddress", "");
      form.setValue("submiting", false);
    } catch (error) {
      console.error(error);
      form.setValue("submiting", false);
    }
  };

  const handleSetJupiterProgram = async (nextJupiterProgramAddress: string) => {
    if (!isValidSolanaAddress(nextJupiterProgramAddress)) return;

    try {
      form.setValue("submiting", true);
      await setJupiterProgram(nextJupiterProgramAddress);
      await getFactorySettings();
      form.setValue("jupiterProgramAddress", "");
      form.setValue("submiting", false);
    } catch (error) {
      console.error(error);
      form.setValue("submiting", false);
    }
  };

  const handleSetSwapExecutor = async (nextSwapExecutorAddress: string) => {
    if (!isValidSolanaAddress(nextSwapExecutorAddress)) return;

    try {
      form.setValue("submiting", true);
      await setSwapExecutor(nextSwapExecutorAddress);
      await getFactorySettings();
      form.setValue("swapExecutorAddress", "");
      form.setValue("submiting", false);
    } catch (error) {
      console.error(error);
      form.setValue("submiting", false);
    }
  };

  const handleMigrateFactory = async ({
    operationCount,
    swapExecutor,
  }: {
    operationCount: string;
    swapExecutor: string;
  }) => {
    if (!isValidSolanaAddress(swapExecutor)) return;
    if (!operationCount || Number(operationCount) < 0) return;

    try {
      form.setValue("submiting", true);
      await migrateFactory({
        operationCount: BigInt(operationCount),
        swapExecutor,
      });
      await getFactorySettings();
      form.setValue("submiting", false);
    } catch (error) {
      console.error(error);
      form.setValue("submiting", false);
    }
  };

  const handleSweepFactoryToken = async ({
    mintAddress,
    amount,
  }: {
    mintAddress: string;
    amount: string;
  }) => {
    if (!isValidSolanaAddress(mintAddress) || !parseFloat(amount)) return;

    try {
      form.setValue("submiting", true);
      await solanaFactoryContract.sweepFactoryToken({ mintAddress, amount });
      form.setValue("sweepMintAddress", "");
      form.setValue("sweepAmount", "");
      form.setValue("submiting", false);
    } catch (error) {
      console.error(error);
      form.setValue("submiting", false);
    }
  };

  const handleChangeFees = async ({
    fees,
    isDeposit,
  }: {
    fees: Fee[];
    isDeposit: boolean;
  }) => {
    try {
      form.setValue("submiting", true);
      await setFeesInfo({ fees, isDeposit });
      await getFactorySettings();
      form.setValue("submiting", false);
    } catch (error) {
      console.error(error);
      form.setValue("submiting", false);
    }
  };

  const handleFeeChange = (
    index: number,
    field: keyof Fee,
    newValue: string,
    feeType: "stakingFeeFlex" | "unstakingFeeFlex"
  ) => {
    if (/^\d*$/.test(newValue)) {
      const currentFees = form.getValues(feeType);
      const updatedFees = currentFees.map((fee, i) =>
        i === index ? { ...fee, [field]: newValue } : fee
      );
      form.setValue(feeType, updatedFees);
    }
  };

  const handleFeeRemove = (
    index: number,
    feeType: "stakingFeeFlex" | "unstakingFeeFlex"
  ) => {
    const currentFees = form.getValues(feeType);
    const updatedFees = currentFees.filter((_, i) => i !== index);
    form.setValue(feeType, updatedFees);
  };

  const handleAddFeeRecord = (
    feeType: "stakingFeeFlex" | "unstakingFeeFlex"
  ) => {
    const currentFees = form.getValues(feeType);
    const newFee: Fee = {
      from: "",
      // to: "",
      percentage: "",
      fixedFee: "",
    };
    form.setValue(feeType, [...currentFees, newFee]);
  };

  const isFactoryOwnerConnected = Boolean(
    factoryOwner &&
      account &&
      (isCurrentSolanaNetwork
        ? factoryOwner === account
        : factoryOwner.toLowerCase() === account.toLowerCase())
  );

  const factoryAddress = isCurrentSolanaNetwork
    ? solanaFactoryContract.factoryConfigAddress
    : currentNetwork?.factoryContractAddress;

  return (
    <form>
      <div className="text-mauve12 mt-3 flex flex-col gap-6">
        <Card>
          <div className="p-4">
            <h2 className="text-mauve12 mb-4 text-[17px] font-medium">
              Creation Factory values
            </h2>
            {isLoadingState ? (
              <Loader />
            ) : (
              <>
                <div className="mb-2 flex gap-3 rounded-md bg-gray-100 p-2">
                  <div>
                    <span>
                      Owner address:{" "}
                      {factoryOwner
                        ? shortenAddress(factoryOwner)
                        : "Not configured"}
                    </span>
                  </div>
                  <div>
                    <span>
                      {isCurrentSolanaNetwork
                        ? "Factory config: "
                        : "Factory address: "}
                      {factoryAddress
                        ? shortenAddress(factoryAddress)
                        : "Not configured"}
                    </span>
                  </div>
                </div>

                <ul className="flex gap-4 [&>li]:flex [&>li]:gap-2 [&>li]:rounded-md [&>li]:bg-gray-200 [&>li]:p-2">
                  <li>
                    <span>Pool Creation Fee:</span>
                    <span>{poolCreationFeeValue}$</span>
                  </li>
                  {!isCurrentSolanaNetwork &&
                    currentNetwork?.chainId === polygonId && (
                      <li>
                        <span>XBR Discount:</span>
                        <span>{xbrFeeValue}%</span>
                      </li>
                    )}
                </ul>

                {isCurrentSolanaNetwork && (
                  <ul className="mt-2 flex gap-4 [&>li]:flex [&>li]:gap-2 [&>li]:rounded-md [&>li]:bg-gray-200 [&>li]:p-2">
                    <li>
                      <span>Treasury:</span>
                      <span>
                        {solanaTreasuryAddress
                          ? shortenAddress(solanaTreasuryAddress)
                          : "Not configured"}
                      </span>
                    </li>
                    <li>
                      <span>Jupiter Program:</span>
                      <span>
                        {solanaJupiterProgramAddress
                          ? shortenAddress(solanaJupiterProgramAddress)
                          : "Not configured"}
                      </span>
                    </li>
                    <li>
                      <span>Swap Executor:</span>
                      <span>
                        {solanaSwapExecutorAddress
                          ? shortenAddress(solanaSwapExecutorAddress)
                          : "Not configured"}
                      </span>
                    </li>
                    <li>
                      <span>Operation Count:</span>
                      <span>{solanaOperationCount || "0"}</span>
                    </li>
                  </ul>
                )}
              </>
            )}
          </div>
        </Card>

        {isFactoryOwnerConnected && !isLoadingState && (
          <>
            <Card>
              <div className="p-4">
                <h2 className="text-mauve12 mb-4 text-[17px] font-medium">
                  Change Pool Creation Fee
                </h2>
                <FormProvider {...form}>
                  <fieldset className="mb-[15px] flex-col">
                    <div className="flex items-end gap-5">
                      <InputField
                        variant="auth"
                        state={form.formState.errors.creationFee && "error"}
                        extra="w-[320px]"
                        label="Pool Creation Fee ($):"
                        placeholder="0"
                        id="creationFee"
                        type="text"
                        {...form.register("creationFee", {})}
                      />

                      <Button
                        className="flex h-12 w-[100px] items-center justify-center"
                        disabled={submiting}
                        onClick={() => handleSetPoolCreationFee(creationFee)}
                      >
                        {submiting ? "Loading..." : "Change"}
                      </Button>
                    </div>
                    {form.formState.errors.creationFee && (
                      <span className="text-sm text-red-500 dark:text-red-400">
                        {form.formState.errors.creationFee.message}
                      </span>
                    )}
                  </fieldset>

                  {!isCurrentSolanaNetwork &&
                    currentNetwork?.chainId === polygonId && (
                      <fieldset className="mb-[15px] flex-col">
                        <div className="flex items-end gap-5">
                          <InputField
                            variant="auth"
                            state={form.formState.errors.xbrDiscount && "error"}
                            extra="w-[320px]"
                            label="XBR Discount (%):"
                            placeholder="0"
                            id="xbrDiscount"
                            type="text"
                            {...form.register("xbrDiscount", {})}
                          />

                          <Button
                            className="flex h-12 w-[100px] items-center justify-center"
                            disabled={submiting}
                            onClick={() =>
                              handleSetXBRDiscounteNumerator(xbrDiscount)
                            }
                          >
                            {submiting ? "Loading..." : "Change"}
                          </Button>
                        </div>
                        {form.formState.errors.xbrDiscount && (
                          <span className="text-sm text-red-500 dark:text-red-400">
                            {form.formState.errors.xbrDiscount.message}
                          </span>
                        )}
                      </fieldset>
                    )}
                </FormProvider>
              </div>
            </Card>

            {isCurrentSolanaNetwork && (
              <Card>
                <div className="p-4">
                  <h2 className="text-mauve12 mb-4 text-[17px] font-medium">
                    Change Solana Factory Addresses
                  </h2>

                  <fieldset className="mb-[15px] flex-col">
                    <div className="flex items-end gap-5">
                      <InputField
                        variant="auth"
                        extra="w-[620px]"
                        label="Treasury Address:"
                        placeholder="Solana address"
                        id="treasuryAddress"
                        type="text"
                        {...form.register("treasuryAddress", {})}
                      />

                      <Button
                        className="flex h-12 w-[100px] items-center justify-center"
                        disabled={submiting || !isValidSolanaAddress(treasuryAddress)}
                        onClick={() => handleSetTreasury(treasuryAddress)}
                      >
                        {submiting ? "Loading..." : "Change"}
                      </Button>
                    </div>
                  </fieldset>

                  <fieldset className="mb-[15px] flex-col">
                    <div className="flex items-end gap-5">
                      <InputField
                        variant="auth"
                        extra="w-[620px]"
                        label="Jupiter Program:"
                        placeholder="Solana program address"
                        id="jupiterProgramAddress"
                        type="text"
                        {...form.register("jupiterProgramAddress", {})}
                      />

                      <Button
                        className="flex h-12 w-[100px] items-center justify-center"
                        disabled={
                          submiting || !isValidSolanaAddress(jupiterProgramAddress)
                        }
                        onClick={() =>
                          handleSetJupiterProgram(jupiterProgramAddress)
                        }
                      >
                        {submiting ? "Loading..." : "Change"}
                      </Button>
                    </div>
                  </fieldset>

                  <fieldset className="mb-[15px] flex-col">
                    <div className="flex items-end gap-5">
                      <InputField
                        variant="auth"
                        extra="w-[620px]"
                        label="Swap Executor:"
                        placeholder="Solana wallet address"
                        id="swapExecutorAddress"
                        type="text"
                        {...form.register("swapExecutorAddress", {})}
                      />

                      <Button
                        className="flex h-12 w-[100px] items-center justify-center"
                        disabled={
                          submiting || !isValidSolanaAddress(swapExecutorAddress)
                        }
                        onClick={() =>
                          handleSetSwapExecutor(swapExecutorAddress)
                        }
                      >
                        {submiting ? "Loading..." : "Change"}
                      </Button>
                    </div>
                  </fieldset>
                </div>
              </Card>
            )}

            {isCurrentSolanaNetwork && (
              <Card>
                <div className="p-4">
                  <h2 className="text-mauve12 mb-4 text-[17px] font-medium">
                    Factory Migration
                  </h2>
                  <fieldset className="mb-[15px] flex-col">
                    <div className="flex flex-col gap-4">
                      <InputField
                        variant="auth"
                        extra="w-[320px]"
                        label="Operation Count:"
                        placeholder={solanaOperationCount || "0"}
                        id="migrationOperationCount"
                        type="text"
                        {...form.register("migrationOperationCount", {})}
                      />
                      <InputField
                        variant="auth"
                        extra="w-[620px]"
                        label="Swap Executor:"
                        placeholder={
                          solanaSwapExecutorAddress || "Solana wallet address"
                        }
                        id="migrationSwapExecutorAddress"
                        type="text"
                        {...form.register("migrationSwapExecutorAddress", {})}
                      />
                      <Button
                        className="flex h-12 w-[160px] items-center justify-center"
                        disabled={
                          submiting ||
                          !isValidSolanaAddress(
                            migrationSwapExecutorAddress ||
                              solanaSwapExecutorAddress
                          ) ||
                          (!migrationOperationCount &&
                            !solanaOperationCount)
                        }
                        onClick={() =>
                          handleMigrateFactory({
                            operationCount:
                              migrationOperationCount || solanaOperationCount,
                            swapExecutor:
                              migrationSwapExecutorAddress ||
                              solanaSwapExecutorAddress,
                          })
                        }
                      >
                        {submiting ? "Loading..." : "Run Migration"}
                      </Button>
                    </div>
                  </fieldset>
                </div>
              </Card>
            )}

            {isCurrentSolanaNetwork && (
              <Card>
                <div className="p-4">
                  <h2 className="text-mauve12 mb-4 text-[17px] font-medium">
                    Sweep factory token to treasury
                  </h2>
                  <fieldset className="mb-[15px] flex-col">
                    <div className="flex flex-col gap-4">
                      <InputField
                        variant="auth"
                        extra="w-[620px]"
                        label="Token Mint:"
                        placeholder="Solana mint address"
                        id="sweepMintAddress"
                        type="text"
                        {...form.register("sweepMintAddress", {})}
                      />
                      <div className="flex items-end gap-5">
                        <InputField
                          variant="auth"
                          extra="w-[320px]"
                          label="Amount:"
                          placeholder="0"
                          id="sweepAmount"
                          type="text"
                          {...form.register("sweepAmount", {})}
                        />
                        <Button
                          className="flex h-12 w-[100px] items-center justify-center"
                          disabled={
                            submiting ||
                            !isValidSolanaAddress(sweepMintAddress) ||
                            !parseFloat(sweepAmount)
                          }
                          onClick={() =>
                            handleSweepFactoryToken({
                              mintAddress: sweepMintAddress,
                              amount: sweepAmount,
                            })
                          }
                        >
                          {submiting ? "Loading..." : "Sweep"}
                        </Button>
                      </div>
                    </div>
                  </fieldset>
                </div>
              </Card>
            )}

            <Card>
              <div className="p-4">
                <h2 className="text-mauve12 mb-4 text-[17px] font-medium">
                  Change factory Staking fees
                </h2>

                {stakingFeeFlex.map((fee, index) => (
                  <FeeComponent
                    key={index}
                    fee={fee}
                    index={index}
                    onChange={(...params) =>
                      handleFeeChange(...params, "stakingFeeFlex")
                    }
                    onRemove={(index) =>
                      handleFeeRemove(index, "stakingFeeFlex")
                    }
                    disabled={submiting}
                  />
                ))}

                <div className="mt-4 flex gap-6">
                  <Button
                    className="flex h-12 w-[100px] items-center justify-center"
                    disabled={submiting}
                    onClick={() =>
                      handleChangeFees({
                        fees: stakingFeeFlex,
                        isDeposit: true,
                      })
                    }
                  >
                    {submiting ? "Loading..." : "Change"}
                  </Button>
                  <Button
                    className="flex h-12 w-fit items-center justify-center"
                    disabled={submiting}
                    onClick={() => handleAddFeeRecord("stakingFeeFlex")}
                  >
                    Add new
                  </Button>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-4">
                <h2 className="text-mauve12 mb-4 text-[17px] font-medium">
                  Change factory Unstaking fees
                </h2>

                {unstakingFeeFlex.map((fee, index) => (
                  <FeeComponent
                    key={index}
                    fee={fee}
                    index={index}
                    onChange={(...params) =>
                      handleFeeChange(...params, "unstakingFeeFlex")
                    }
                    onRemove={(index) =>
                      handleFeeRemove(index, "unstakingFeeFlex")
                    }
                    disabled={submiting}
                  />
                ))}

                <div className="mt-4 flex gap-6">
                  <Button
                    className="flex h-12 w-[100px] items-center justify-center"
                    disabled={submiting}
                    onClick={() =>
                      handleChangeFees({
                        fees: unstakingFeeFlex,
                        isDeposit: false,
                      })
                    }
                  >
                    {submiting ? "Loading..." : "Change"}
                  </Button>
                  <Button
                    className="flex h-12 w-fit items-center justify-center"
                    disabled={submiting}
                    onClick={() => handleAddFeeRecord("unstakingFeeFlex")}
                  >
                    Add new
                  </Button>
                </div>
              </div>
            </Card>

            {!isCurrentSolanaNetwork && (
              <Card>
                <div className="p-4">
                  <h2 className="text-mauve12 mb-4 text-[17px] font-medium">
                    Transfer given token from factory contract to the owner
                  </h2>
                  <fieldset className="mb-[15px] flex-col">
                    <div className="flex items-end gap-5">
                      <InputField
                        variant="auth"
                        state={form.formState.errors.creationFee && "error"}
                        extra="w-[320px]"
                        label="Address:"
                        placeholder="0x"
                        id="address"
                        type="text"
                        {...form.register("address", {})}
                      />

                      <Button
                        className="flex h-12 w-[100px] items-center justify-center"
                        disabled={submiting || !isAddress(address)}
                        onClick={() => handleSweepToken(address)}
                      >
                        {submiting ? "Loading..." : "Sweep"}
                      </Button>
                    </div>
                    {form.formState.errors.address && (
                      <span className="text-sm text-red-500 dark:text-red-400">
                        {form.formState.errors.address.message}
                      </span>
                    )}
                  </fieldset>
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </form>
  );
};
